// apps/web/src/features/onboarding/api/onboardingApi.ts
import type {
  Step6Input,
} from "./../../../shared/schemas/index";

const RAW_API_BASE_URL =
  import.meta.env.VITE_ONBOARDING_API_URL || "http://localhost:4000/dev";

if (!RAW_API_BASE_URL) {
  throw new Error(
    "VITE_ONBOARDING_API_URL is not defined in environment variables"
  );
}

const API_BASE = RAW_API_BASE_URL.endsWith("/")
  ? RAW_API_BASE_URL
  : `${RAW_API_BASE_URL}/`;

export interface CompanyFormPayload {
  taxId: string;
  formData: {
    step1: Record<string, unknown>;
    step2: Record<string, unknown> & {
      billingStreetNumber: string;
      companyStreetNumber?: string;
    };
    step3: Record<string, unknown>;
    step4: Record<string, unknown>;
    step5: Record<string, unknown>;
    step6: Step6Input;
  };
}

function transformStep1DataForBackend(
  step1: CompanyFormPayload["formData"]["step1"]
): Record<string, unknown> {
  const { fleetSize, ...rest } = step1 as Record<string, unknown> & {
    fleetSize?: unknown;
  };

  return {
    ...rest,
    fleetNumber: fleetSize,
  };
}

// Se l'indirizzo aziendale non e' diverso, riallinea i campi company con i billing.
function transformStep2DataForBackend(step2: CompanyFormPayload["formData"]["step2"]): Omit<CompanyFormPayload["formData"]["step2"], 'billingStreetNumber' | 'companyStreetNumber'> & {
  billingStreetNumber: number;
  companyStreetNumber: number | "";
} {
  const companyStreetNumberRaw = String(step2.companyStreetNumber ?? "").trim();

  return {
    ...step2,
    billingStreetNumber: Number(step2.billingStreetNumber),
    companyStreetNumber:
      companyStreetNumberRaw === "" ? "" : Number(companyStreetNumberRaw),
  };
}

export const TEMPORARY_ISSUE_MESSAGE =
  "We couldn’t complete the request. Please try again in a moment.";

export class RateLimitRetryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RateLimitRetryError";
  }
}

export class TemporaryServerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TemporaryServerError";
  }
}


export class TemporaryNetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TemporaryNetworkError";
  }
}

/**
 * 503 Maintenance mode esplicito
 */
export class MaintenanceModeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MaintenanceModeError";
  }
}

const RETRY_DELAYS_MS = [2000, 4000, 6000, 8000]; 

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildUrl(path: string): string {
  const normalized = path.replace(/^\/+/, "");
  return new URL(normalized, API_BASE).toString();
}

/**
 * GET v1/b2b/onboarding-form/healthcheck
 * Nuova logica:
 * - 200 => ok 
 * - >= 500 => maintenance page

 */
export async function checkHealthForMaintenance(
  opts?: { timeoutMs?: number }
): Promise<"ok" | "maintenance"> {
  const url = buildUrl("v1/b2b/onboarding-form/healthcheck");

  const timeoutMs = opts?.timeoutMs ?? 5000;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });

    // SOLO >= 500 => maintenance
    if (res.status >= 500) return "maintenance";

    // 200/3xx/4xx => flusso normale
    return "ok";
  } catch {
    // network / timeout => flusso normale
    return "ok";
  } finally {
    clearTimeout(timeout);
  }
}


/**
 * POST v1/b2b/onboarding-form/company
 * - 429: retry fino a 4 volte (totale 5 tentativi) con delay 2/4/6/8 secondi
 * - 503: maintenance page
 * - >=500: maintenance page

 */
export async function submitCompanyForm(
  payload: CompanyFormPayload
): Promise<void> {
  const url = buildUrl("v1/b2b/onboarding-form/company");
  const totalAttempts = 1 + RETRY_DELAYS_MS.length;

  // Trasformo i dati di step2 convertendo billingStreetNumber e companyStreetNumber da stringa a numero
  const transformedPayload = {
    ...payload,
    formData: {
      ...payload.formData,
      step1: transformStep1DataForBackend(payload.formData.step1),
      step2: transformStep2DataForBackend(payload.formData.step2),
    },
  };

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    let res: Response;

    try {
      res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transformedPayload),
      });
    } catch {
      throw new TemporaryNetworkError(TEMPORARY_ISSUE_MESSAGE);
    }

    // 429 => retry fino a esaurimento tentativi
    if (res.status === 429) {
      const isLastAttempt = attempt === totalAttempts - 1;
      if (isLastAttempt) {
        throw new RateLimitRetryError(TEMPORARY_ISSUE_MESSAGE);
      }
      await sleep(RETRY_DELAYS_MS[attempt]);
      continue;
    }

    // 503 => maintenance esplicita
    if (res.status === 503) {
      throw new MaintenanceModeError("Maintenance mode enabled");
    }

    // >= 500 => maintenance
    if (res.status >= 500) {
      throw new TemporaryServerError(TEMPORARY_ISSUE_MESSAGE);
    }

    // altri errori => no retry
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Failed to submit company form (${res.status}): ${text || res.statusText}`
      );
    }

    // OK
    return;
  }
}
